<template>
  <div class="page">
    <!-- HEADER -->
    <site-header is-static />

    <v-main>
      <!-- MAIN -->
      <div
        v-if="!isLoading"
        class="site-width"
      >
        <validation-observer
          ref="formValidationObserver"
          v-slot="formObserver"
          slim
        >
          <!-- BREADCRUMBS -->
          <steps
            class="breadcrumbs"
            :steps="adjustedMetaData"
            @go-to-step="(val) => onGoToStep({...val, formObserver})"
          />

          <qz-cta
            class="mb-4"
            :title="ctaTitle"
            :current-step="currentStep"
          >
            <template #firstStepNote>
              <landlords-suggestion-help-text class="mb-6" />
            </template>
            <template #firstStepInfo>
              <p>
                <strong>Let's save some money!</strong>
                Take care to answer questions accurately. Incomplete information could see your policy cancelled or a claim rejected.
              </p>
            </template>
          </qz-cta>

          <!-- FORM -->
          <form
            id="form"
            novalidate
            sticky-container
            @submit.prevent
            @keydown.enter.prevent
          >
            <!-- FORM STEPS -->
            <div class="form-wrapper">
              <component
                :is="stepComponent"
                ref="step"
                :form-observer="patchObserver(formObserver)"
                :form="form"
                @step-data="stepData = $event"
                @tracking="gtmTracking"
              />
            </div>

            <!-- ERRORS SECTION -->
            <core-step-errors
              :active-step="activeStep"
              :structure="homeStructure"
              :form-observer="formObserver"
              :component-key="componentKey"
              :is-validated="isValidated"
              :step-ref="$refs.step"
            />

            <qz-form-actions
              ref="formActions"
              :current-step="currentStep"
              :form-observer="formObserver"
              :component-key="componentKey"
              :show-actions="showActions"
              :is-last-step="isLastStep"
              @prev-step="prevStep(formObserver)"
              @next-step="() => {
                disableBreadcrumbsForwardStepsNavigation(currentStep, form, stepData);
                nextStep(formObserver, saveBreadcrumbsForwardStepsNavigationDisablingFieldsValues);
              }"
              @captcha-submit="validateBeforeRecaptcha(formObserver)"
            />
          </form>
        </validation-observer>
      </div>

      <quote-limit-exceed-popup v-model="quoteLimitExceeded" />

      <template v-if="!isLoading">
        <!-- FOOTER -->
        <site-footer
          v-if="config.partnerFooter"
          role="footer"
        >
          <div v-html="config.partnerFooterText || ''" />
        </site-footer>

        <branding-logo
          v-if="config.brandingLogo"
          :branding-type="config.brandingType"
        />

        <bespoke-footer-information
          v-if="showBespokeFooterInformation"
          :content="config.bespokeFooterInformation"
        />
      </template>
    </v-main>

    <!-- LOADING OVERLAY -->
    <page-loader :value="isLoading || isProcessing" />
  </div>
</template>

<script>
import { inject } from '@vue/composition-api';

import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneForm from '@/composables/useQuotezoneForm';
import useBreadcrumbsForwardNavigationController from '@/composables/useBreadcrumbsForwardNavigationController';
import { getQuotePayload } from '@/payloadTemplates/home';
import { homeStructure } from '@/helpers/products/home';

import Steps from '@/components/Steps/Steps.vue';

import Step1 from '@/views/home/steps/Step1.vue';
import Step2 from '@/views/home/steps/Step2.vue';
import Step3 from '@/views/home/steps/Step3.vue';

import LandlordsSuggestionHelpText from '@/components/home/Form/LandlordsSuggestionHelpText.vue';
import QzCta from '@/components/QzCta/QzCta.vue';
import QzFormActions from '@/components/QzFormActions.vue';
import CoreStepErrors from '@/components/StepErrors/CoreStepErrors.vue';

export default {
  name: 'page-home-insurance',
  metaInfo () {
    const defaultTitle = `${this.config.partnerText} | Home Insurance`;
    return {
      title: this.config.partnerPageFormTitle || defaultTitle,
    };
  },
  components: {
    CoreStepErrors,
    QzFormActions,
    Steps,
    Step1,
    Step2,
    Step3,
    LandlordsSuggestionHelpText,
    QzCta,
  },
  computed: {
    ctaTitle () {
      return this.adjustedMetaData.map(step => step.stepLabel)[this.currentStep - 1];
    },
    adjustedMetaData () {
      const isCoverTypeBuildingsOnly = this.form?.home.propertyCoverType === 'B';

      if (!isCoverTypeBuildingsOnly) return this.formConfigData;
      return this.formConfigData.map(step => {
        if (step.stepId === 'content') {
          step.stepLabel = 'Occupancy & Security';
        }
        return step;
      });
    },
  },
  async created () {
    await this.init();

    // Object with fields and their initial values that disable the breadcrumbs forward page navigation if changed
    this.setBreadcrumbsForwardStepsNavigationDisablingFields({
      propertyCoverType: this.form?.home?.propertyCoverType,
    });
  },
  setup (props, context) {
    return {
      ...useQuotezoneCommon(null, context),
      ...useQuotezoneForm({
        payload: getQuotePayload(inject('config')),
        structure: homeStructure,
      }, context),
      ...useBreadcrumbsForwardNavigationController({
        structure: homeStructure,
      }, context),
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/page-width.scss';

.form-wrapper {
  padding: 5px 0;
  background: #fff;
  border: 1px solid $clr-heather;
  border-radius: 6px;
}
</style>

<style lang='scss'>
.additional {
  @media screen and (max-width: 600px) {
    .additional-item {
      flex-wrap: wrap;
      padding: 12px 8px;
    }

    .item-icon {
      width: 23px;
      height: 23px;
      margin-right: 12px;
    }

    .item-content,
    .v-btn__content {
      font-size: 12px;
    }

    .v-icon {
      font-size: 14px;
      margin-left: 4px;
    }

    .item-content {
      flex: 1 1;
      margin-right: 12px;
    }

    .v-btn {
      justify-self: end;
    }
  }
}

.radio-item .indicator-icon svg {
  width: 30px !important;
}
</style>

<style lang="scss" scoped src="@/assets/scss/core-products/product-insurance.scss"/>
<style lang="scss" scoped src="@/assets/scss/shared/breadcrumbs.scss"/>
